/**
 * useCameraLocations Hook
 *
 * React Query hook for fetching VMS camera locations for the dispatch map.
 * Returns cameras with coordinates that can be displayed as map markers.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axiosInstance from '../../../../../api/axiosInstance';
import { CameraLocation, CameraStatus } from '../../components/guardLocation/types';

/** Query key for camera locations */
export const CAMERA_LOCATIONS_QUERY_KEY = ['cameraLocations'];

/**
 * Cameras are static infrastructure — fetch once, never poll.
 * Cache is only invalidated by VMS socket events (e.g. vms:incident-alert).
 */

export interface UseCameraLocationsOptions {
  /** Enable or disable the query (default: true) */
  enabled?: boolean;
  /** Override polling interval (ms) */
  refetchInterval?: number;
}

export interface UseCameraLocationsResult {
  /** Cameras with coordinates */
  cameras: CameraLocation[];
  /** Total camera count */
  count: number;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress */
  isFetching: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
  /** Manually trigger a refresh */
  refetch: () => void;
}

/**
 * Fetch camera locations from the CANVMS API
 */
/** Map VMS camera status to frontend status */
function mapCameraStatus(status: string | null | undefined): CameraStatus {
  if (!status) return 'unknown';
  const s = status.toLowerCase();
  if (s === 'watching' || s === 'recording' || s === 'online') return 'online';
  if (s === 'offline' || s === 'disabled' || s === 'stopped') return 'offline';
  return 'unknown';
}

async function fetchCameraLocations(): Promise<{ cameras: CameraLocation[] }> {
  const baseURL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:10000/api3.2/canvms'
    : '/api3.2/canvms';

  const response = await axiosInstance.get(`${baseURL}/map`, {
    withCredentials: true,
  });

  const raw = response.data?.data ?? response.data ?? { cameras: [] };

  // Map backend field names to frontend CameraLocation interface
  const cameras: CameraLocation[] = (raw.cameras ?? []).map((c: any) => ({
    id: c.camera_id,
    name: c.camera_name,
    display_name: c.camera_display_name,
    site_id: c.site_id,
    site_name: c.site_name ?? c.site_display_name,
    customer_id: c.customer_id ?? null,
    customer_name: c.customer_name ?? null,
    address_id: c.address_id ?? null,
    address_title: c.address_title ?? null,
    latitude: c.latitude,
    longitude: c.longitude,
    status: mapCameraStatus(c.camera_status),
    stream_type: c.stream_type ?? 'mjpeg',
    alert_count: c.active_incident_count ?? 0,
    ptz_capable: false,
  }));

  return { cameras };
}

/**
 * Hook to fetch camera locations for the dispatch map
 *
 * @param options - Configuration options
 * @returns Camera locations with loading/error states
 */
export function useCameraLocations(
  options: UseCameraLocationsOptions = {}
): UseCameraLocationsResult {
  const {
    enabled = true,
  } = options;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: CAMERA_LOCATIONS_QUERY_KEY,
    queryFn: fetchCameraLocations,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Filter to only cameras with valid coordinates
  const cameras = useMemo(() => {
    if (!data?.cameras) return [];
    return data.cameras.filter(
      (camera) =>
        camera.latitude != null &&
        camera.longitude != null &&
        camera.latitude !== 0 &&
        camera.longitude !== 0
    );
  }, [data?.cameras]);

  return {
    cameras,
    count: cameras.length,
    isLoading,
    isFetching,
    isError,
    error: error instanceof Error ? error : null,
    refetch,
  };
}

export default useCameraLocations;
